import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../../supabaseClient";
import LoaderBox from "./LoaderBox";

const HomeGroups = ({ turnirId, activeGroupFromSearch }) => {
  const [activeTab, setActiveTab] = useState("seniori");
  const [hasVeterani, setHasVeterani] = useState(false);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openGroups, setOpenGroups] = useState([]);
  const [hideDataMode, setHideDataMode] = useState(false);
  const groupRefs = useRef({});

  // 1. Provjera postavki turnira
  useEffect(() => {
    const fetchTurnirConfig = async () => {
      if (!turnirId) return;
      try {
        const { data: settings } = await supabase
          .from("admin_turniri")
          .select("prikazi_timove, hide_data_mode, features")
          .eq("id", turnirId)
          .maybeSingle();

        if (settings) {
          setHasVeterani(settings.features?.veterani === true);
          const isLocalhost =
            window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1";
          setHideDataMode(
            isLocalhost
              ? false
              : !settings.prikazi_timove || settings.hide_data_mode,
          );
        }
      } catch (err) {
        console.error("Greška pri dohvaćanju postavki:", err);
      }
    };

    fetchTurnirConfig();
  }, [turnirId]);

  // 2. Dohvat timova i utakmica ovisno o aktivnom tabu
  useEffect(() => {
    const fetchData = async () => {
      if (!turnirId) return;
      setLoading(true);
      try {
        const isVet = activeTab === "veterani";
        const teamsTable = isVet ? "turniri_veterani_timovi" : "turniri_timovi";
        const matchesTable = isVet ? "turniri_veterani" : "turniri_utakmice";

        const { data: teamsData } = await supabase
          .from(teamsTable)
          .select("*")
          .eq("turnir_id", turnirId);

        const { data: matchesData } = await supabase
          .from(matchesTable)
          .select("*")
          .eq("tournament_id", turnirId)
          .eq("is_completed", true)
          .neq("group_name", "Završnica");

        const stats = {};

        teamsData?.forEach((t) => {
          stats[t.id] = {
            ...t,
            played: 0,
            points: 0,
            goals_for: 0,
            goals_against: 0,
            gd: 0,
          };
        });

        matchesData?.forEach((m) => {
          const tA = stats[m.team_a_id];
          const tB = stats[m.team_b_id];

          if (tA && tB) {
            tA.played += 1;
            tB.played += 1;
            tA.goals_for += m.scorea || 0;
            tA.goals_against += m.scoreb || 0;
            tB.goals_for += m.scoreb || 0;
            tB.goals_against += m.scorea || 0;

            if (m.scorea > m.scoreb) tA.points += 3;
            else if (m.scorea < m.scoreb) tB.points += 3;
            else {
              tA.points += 1;
              tB.points += 1;
            }
          }
        });

        const processedTeams = Object.values(stats).map((t) => ({
          ...t,
          gd: t.goals_for - t.goals_against,
        }));

        setTeams(processedTeams);
      } catch (err) {
        console.error("Greška:", err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [turnirId, activeTab]);

  useEffect(() => {
    if (activeGroupFromSearch) {
      setOpenGroups((prev) =>
        prev.includes(activeGroupFromSearch)
          ? prev
          : [...prev, activeGroupFromSearch],
      );

      setTimeout(() => {
        const element = groupRefs.current[activeGroupFromSearch];
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.classList.add("highlight-group");
          setTimeout(() => element.classList.remove("highlight-group"), 2000);
        }
      }, 300);
    }
  }, [activeGroupFromSearch]);

  const toggleGroup = (groupName) => {
    setOpenGroups((prev) =>
      prev.includes(groupName)
        ? prev.filter((g) => g !== groupName)
        : [...prev, groupName],
    );
  };

  // Dinamičko grupiranje timova i filtriranje onih koji nemaju grupu
  const realGroupedTeams = teams.reduce((acc, team) => {
    const group = team.group_name || team.grupa;
    if (group && group !== "Bez grupe" && group !== "Završnica") {
      if (!acc[group]) acc[group] = [];
      acc[group].push(team);
    }
    return acc;
  }, {});

  // Ovdje uzimamo SAMO ona imena grupa koja stvarno postoje u podacima i sortiramo ih abecedno (npr. A, B)
  const dynamicGroupNames = Object.keys(realGroupedTeams).sort();

  const finalGroups = dynamicGroupNames.map((name) => {
    const teamsInGroup = hideDataMode ? [] : realGroupedTeams[name] || [];

    const sortedTeamsInGroup = [...teamsInGroup].sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      if (b.gd !== a.gd) return b.gd - a.gd;
      if (b.goals_for !== a.goals_for) return b.goals_for - a.goals_for;
      return a.name.localeCompare(b.name, "hr");
    });

    // Prikazujemo samo sortirane ekipe koje stvarno postoje, bez automatskog dodavanja praznih mjesta
    const displayTeams = [...sortedTeamsInGroup];

    return { groupName: name, teams: displayTeams };
  });

  if (loading) return <LoaderBox text="Učitavanje tablica..." />;

  return (
    <>
      {hasVeterani && (
        <div className="groups-category-switcher-bar">
          <button
            className={`switcher-btn ${activeTab === "seniori" ? "active" : ""}`}
            onClick={() => setActiveTab("seniori")}
          >
            Seniori
          </button>
          <button
            className={`switcher-btn ${activeTab === "veterani" ? "active" : ""}`}
            onClick={() => setActiveTab("veterani")}
          >
            Veterani
          </button>
        </div>
      )}
      <div className="groups-container">
        {finalGroups.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              color: "#aaa",
              padding: "30px",
              fontStyle: "italic",
            }}
          >
            Grupe još nisu formirane.
          </div>
        ) : (
          finalGroups.map(({ groupName, teams: groupTeams }) => (
            <div
              key={groupName}
              ref={(el) => (groupRefs.current[groupName] = el)}
              className={`group-card ${openGroups.includes(groupName) ? "open" : ""}`}
            >
              <div
                className="group-header"
                onClick={() => toggleGroup(groupName)}
              >
                <h4>Grupa {groupName}</h4>
                <i
                  className={`fi ${openGroups.includes(groupName) ? "fi-rs-angle-small-up" : "fi-rs-angle-small-down"}`}
                ></i>
              </div>

              <div className="group-content">
                <div className="standings-table">
                  <div className="table-head">
                    <span>#</span>
                    <span>EKIPA</span>
                    <span>OU</span>
                    <span>GOL</span>
                    <span>BOD</span>
                  </div>
                  <div className="table-body">
                    {groupTeams.map((team, tIdx) => (
                      <div
                        className={`table-body-club ${team.isPlaceholder ? "placeholder-row" : ""}`}
                        key={team.id}
                      >
                        <span
                          className={`rank-circle ${tIdx < 2 && !team.isPlaceholder ? "pass" : "fail"}`}
                        >
                          {tIdx + 1}
                        </span>

                        <span className="team-name">{team.name}</span>
                        <span className="played">{team.played}</span>
                        <span className="ga">
                          {team.goals_for}:{team.goals_against}
                        </span>
                        <span className="pts">{team.points}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
};

export default HomeGroups;
